import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useDisplayNameSetup } from '@/hooks/useDisplayNameSetup';
import { RequestForm } from '@/modules/requests/ui/RequestForm';
import { AuthModal } from '@/app/components/AuthModal';
import { DisplayNameSetup } from '@/app/components/DisplayNameSetup';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';

export function SubmitIdeaPage() {
  const { user, loading } = useAuth();
  const { needsSetup, isChecking, markSetupComplete } = useDisplayNameSetup();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleRequestCreated = (newRequest) => {
    // Navigate to the new request detail page
    navigate(`/request/${newRequest.id}`);
  };

  const handleBack = () => {
    // Always go back to homepage since we integrated discovery there
    navigate('/');
  };

  const handleSignInNeeded = () => {
    console.log('SubmitIdeaPage: User needs to sign in, opening auth modal');
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    console.log('SubmitIdeaPage: Closing auth modal');
    setShowAuthModal(false);
  };

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-t-transparent border-purple-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }



  // If authenticated user needs profile setup, show the setup modal
  if (user && needsSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <Header onSignInNeeded={handleSignInNeeded} />
        
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
            {/* Header */}
            <div className="mb-12">
              <button
                onClick={handleBack}
                className="group inline-flex items-center text-indigo-600 hover:text-indigo-800 font-semibold mb-8 transition-all duration-300 transform hover:scale-105"
              >
                <svg className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to App Discovery
              </button>
              
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
                  <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Complete Your Profile
                  </span>
                </h1>
                <p className="text-xl sm:text-2xl text-gray-600 font-light">
                  Set up your display name to submit app ideas
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <DisplayNameSetup
          user={user}
          onComplete={markSetupComplete}
        />
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-violet-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <Header onSignInNeeded={handleSignInNeeded} />
      
      <div className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="mb-12">
            <button
              onClick={handleBack}
              className="group inline-flex items-center text-indigo-600 hover:text-indigo-800 font-semibold mb-8 transition-all duration-300 transform hover:scale-105"
            >
              <svg className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to App Discovery
            </button>
          </div>

          {/* Request Form */}
          <div className="max-w-4xl mx-auto">
                      <RequestForm
            onRequestCreated={handleRequestCreated}
            onCancel={handleBack}
            onAuthRequired={handleSignInNeeded}
            prefilledDescription={location.state?.prefilledDescription}
            fromDiscovery={location.state?.fromDiscovery}
          />
          </div>
        </div>
      </div>
      <Footer />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
      />
    </div>
  );
}